/**
 * MapPropertyCard - Mobile
 * 
 * Property card overlay that appears when a map marker is selected.
 * Expandable to show detailed pricing and parcel information.
 * Uses design tokens exclusively - no hardcoded values.
 */

import React, { useState } from 'react';
import { View, Text, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon, border, shadow } from '../../../shared/tokens/mobile';
import { OpportunityStatus } from '../../../shared/types/opportunity';
import { AnimatedPressable } from '../mobile/AnimatedPressable';

interface MapPropertyCardProps {
  name: string;
  address: string | null;
  askingPrice: number | null;
  taxValue: number | null;
  acres: number | null;
  status: OpportunityStatus;
  onPress: () => void;
  onClose: () => void;
  showParcels?: boolean;
  onToggleParcels?: () => void;
  parcelLoading?: boolean;
  // Extended data for expanded view
  county?: string | null;
  owner?: string | null;
  parcelId?: string | null;
}

// Map status to semantic token colors
function getStatusStyle(status: OpportunityStatus): { bg: string; text: string; label: string } {
  switch (status) {
    case 'evaluating':
      return { bg: color.infoLight, text: color.info, label: 'Evaluating' };
    case 'under_contract':
      return { bg: color.warningLight, text: color.warning, label: 'Under Contract' };
    case 'owned':
      return { bg: color.successLight, text: color.success, label: 'Owned' };
    case 'selling':
      return { bg: color.warningLight, text: color.warning, label: 'Selling' };
    case 'sold':
      return { bg: color.backgroundSecondary, text: color.textMuted, label: 'Sold' };
    case 'passed':
      return { bg: color.backgroundSecondary, text: color.textMuted, label: 'Passed' };
    default:
      return { bg: color.backgroundSecondary, text: color.textSecondary, label: status };
  }
}

function formatCurrency(value: number | null | undefined): string {
  if (value == null) return '—';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${value.toLocaleString()}`;
}

function formatPerAcre(value: number | null | undefined, acres: number | null | undefined): string {
  if (value == null || acres == null || acres <= 0) return '—';
  const perAcre = value / acres;
  if (perAcre >= 1000000) return `$${(perAcre / 1000000).toFixed(1)}M/ac`;
  if (perAcre >= 1000) return `$${Math.round(perAcre / 1000)}K/ac`;
  return `$${Math.round(perAcre).toLocaleString()}/ac`;
}

export function MapPropertyCard({ 
  name, 
  address, 
  askingPrice, 
  taxValue,
  acres, 
  status, 
  onPress, 
  onClose,
  showParcels,
  onToggleParcels,
  parcelLoading,
  county,
  owner,
  parcelId,
}: MapPropertyCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const statusStyle = getStatusStyle(status);

  // Minimized state - show only a small indicator
  if (minimized) {
    return (
      <Pressable 
        style={styles.minimizedContainer}
        onPress={() => setMinimized(false)}
        accessibilityLabel={`Expand ${name} card`}
        accessibilityRole="button"
      >
        <View style={[styles.minimizedBadge, { backgroundColor: statusStyle.text }]}>
          <Ionicons name="home" size={icon.md} color={color.textInverse} />
        </View>
        <View style={styles.minimizedDot} />
      </Pressable>
    );
  }

  return (
    <View style={styles.container}>
      <View style={[styles.card, expanded && styles.cardExpanded]}>
        {/* Status indicator */}
        <View style={[styles.statusIndicator, { backgroundColor: statusStyle.text }]} />
        
        {/* Header - always visible */}
        <AnimatedPressable 
          onPress={onPress} 
          style={styles.header}
          accessibilityRole="button" 
          accessibilityLabel={`View ${name}`}
        >
          <View style={styles.headerContent}>
            <Text style={styles.title} numberOfLines={1}>{name}</Text>
            {address && (
              <Text style={styles.address} numberOfLines={1}>{address}</Text>
            )}
          </View>
          <Ionicons name="chevron-forward" size={icon.md} color={color.textMuted} />
        </AnimatedPressable>

        {/* Compact metrics row */}
        <View style={styles.metricsRow}>
          <View style={styles.metric}>
            <Text style={styles.metricValue}>{formatCurrency(askingPrice)}</Text>
            <Text style={styles.metricLabel}>Asking</Text>
          </View>
          <View style={styles.metricDivider} />
          <View style={styles.metric}>
            <Text style={styles.metricValue}>{acres?.toFixed(1) ?? '—'}</Text>
            <Text style={styles.metricLabel}>Acres</Text>
          </View>
          <View style={styles.metricDivider} />
          <View style={styles.metric}>
            <Text style={styles.metricValue}>{formatPerAcre(askingPrice, acres)}</Text>
            <Text style={styles.metricLabel}>$/Acre</Text>
          </View>
          <View style={[styles.statusBadge, { backgroundColor: statusStyle.bg }]}>
            <Text style={[styles.statusText, { color: statusStyle.text }]}>
              {statusStyle.label}
            </Text>
          </View>
        </View>

        {/* Expanded content */}
        {expanded && (
          <View style={styles.expandedContent}>
            {/* Tax value section */}
            <View style={styles.section}>
              <Text style={styles.sectionLabel}>Tax Assessed</Text>
              <View style={styles.priceRow}>
                <Text style={styles.priceValue}>{formatCurrency(taxValue)}</Text>
                <Text style={styles.pricePerAcre}>{formatPerAcre(taxValue, acres)}</Text>
              </View>
            </View>

            {/* Parcel details */}
            {(parcelId || owner || county) && (
              <View style={styles.section}>
                <Text style={styles.sectionLabel}>Parcel Info</Text>
                {parcelId && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Parcel ID</Text>
                    <Text style={styles.detailValue}>{parcelId}</Text>
                  </View>
                )}
                {owner && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>Owner</Text>
                    <Text style={styles.detailValue} numberOfLines={1}>{owner}</Text>
                  </View>
                )}
                {county && (
                  <View style={styles.detailRow}>
                    <Text style={styles.detailLabel}>County</Text>
                    <Text style={styles.detailValue}>{county}</Text>
                  </View>
                )}
              </View>
            )}
          </View>
        )}

        {/* Expand/collapse toggle */}
        <Pressable 
          onPress={() => setExpanded(!expanded)} 
          style={styles.expandButton}
          accessibilityLabel={expanded ? 'Show less' : 'Show more'}
        >
          <Text style={styles.expandText}>{expanded ? 'Less' : 'More'}</Text>
          <Ionicons 
            name={expanded ? 'chevron-up' : 'chevron-down'} 
            size={icon.sm} 
            color={color.primary} 
          />
        </Pressable>
      </View>

      {/* Parcel toggle button */}
      {onToggleParcels && (
        <Pressable 
          onPress={onToggleParcels} 
          style={[styles.actionButton, styles.parcelButton, showParcels && styles.actionButtonActive]}
          accessibilityLabel={showParcels ? 'Hide parcel lines' : 'Show parcel lines'}
          disabled={parcelLoading}
        >
          {parcelLoading ? (
            <ActivityIndicator size="small" color={color.primary} />
          ) : (
            <Ionicons 
              name={showParcels ? 'layers' : 'layers-outline'} 
              size={icon.md} 
              color={showParcels ? color.primary : color.textMuted} 
            />
          )}
        </Pressable>
      )}

      {/* Minimize button */}
      <Pressable 
        onPress={() => setMinimized(true)} 
        style={[styles.actionButton, styles.minimizeButton]} 
        accessibilityLabel="Minimize card"
      >
        <Ionicons name="remove" size={icon.sm} color={color.textMuted} />
      </Pressable>

      {/* Close button */}
      <Pressable onPress={onClose} style={[styles.actionButton, styles.closeButton]} accessibilityLabel="Close card">
        <Ionicons name="close" size={icon.sm} color={color.textMuted} />
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: space.lg,
    left: space.md,
    right: space.md,
    zIndex: 10,
  },
  card: {
    backgroundColor: color.surface,
    borderRadius: radius.lg,
    borderWidth: border.width.thin,
    borderColor: color.border,
    overflow: 'hidden',
    ...shadow.sm,
  },
  cardExpanded: {
    ...shadow.sm,
  },
  statusIndicator: {
    width: space.xs,
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    borderTopLeftRadius: radius.lg,
    borderBottomLeftRadius: radius.lg,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: space.md,
    paddingLeft: space.md + space.xs,
    gap: space.sm,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontSize: text.body.fontSize,
    fontWeight: text.cardTitle.fontWeight,
    color: color.textPrimary,
  },
  address: {
    fontSize: text.caption.fontSize,
    color: color.textSecondary,
    marginTop: 2,
  },
  metricsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: space.md,
    paddingBottom: space.sm,
    gap: space.sm,
  },
  metric: {
    alignItems: 'center',
  },
  metricValue: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  metricLabel: {
    fontSize: text.tabLabel.fontSize,
    color: color.textMuted,
    marginTop: 1,
  },
  metricDivider: {
    width: 1,
    height: space.lg,
    backgroundColor: color.border,
  },
  statusBadge: {
    marginLeft: 'auto',
    paddingHorizontal: space.sm,
    paddingVertical: space.xs / 2,
    borderRadius: radius.sm,
  },
  statusText: {
    fontSize: text.tabLabel.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    textTransform: 'uppercase',
  },
  expandedContent: {
    paddingHorizontal: space.md,
    paddingTop: space.sm,
    borderTopWidth: 1,
    borderTopColor: color.border,
  },
  section: {
    marginBottom: space.md,
  },
  sectionLabel: {
    fontSize: text.caption.fontSize,
    fontWeight: '600',
    color: color.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: space.xs,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: space.sm,
  },
  priceValue: {
    fontSize: text.sectionTitle.fontSize,
    fontWeight: text.sectionTitle.fontWeight,
    color: color.textPrimary,
  },
  pricePerAcre: {
    fontSize: text.caption.fontSize,
    color: color.textSecondary,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: space.xs / 2,
  },
  detailLabel: {
    fontSize: text.caption.fontSize,
    color: color.textSecondary,
  },
  detailValue: {
    fontSize: text.caption.fontSize,
    color: color.textPrimary,
    fontWeight: text.bodyMedium.fontWeight,
    flex: 1,
    textAlign: 'right',
    marginLeft: space.sm,
  },
  expandButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: space.sm,
    gap: space.xs,
    borderTopWidth: 1,
    borderTopColor: color.border,
  },
  expandText: {
    fontSize: text.caption.fontSize,
    color: color.primary,
    fontWeight: text.bodyMedium.fontWeight,
  },
  actionButton: {
    position: 'absolute',
    width: space.xl,
    height: space.xl,
    borderRadius: radius.full,
    backgroundColor: color.surface,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: border.width.thin,
    borderColor: color.border,
    ...shadow.sm,
  },
  closeButton: {
    top: -space.sm,
    right: -space.sm,
  },
  parcelButton: {
    top: -space.sm,
    left: -space.sm,
  },
  actionButtonActive: {
    backgroundColor: color.primaryLight,
    borderColor: color.primary,
  },
  minimizeButton: {
    top: -space.sm,
    right: space.xl,
  },
  // Minimized state styles
  minimizedContainer: {
    position: 'absolute',
    bottom: space.lg,
    right: space.md + 56, // Position to the left of FAB
    zIndex: 10,
    alignItems: 'center',
  },
  minimizedBadge: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    ...shadow.sm,
  },
  minimizedDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: color.surface,
    marginTop: -4,
    borderWidth: 2,
    borderColor: color.primary,
  },
});
